// approver-onboarding-modal/steps/ManualAddingStep.tsx
import React from 'react'
import { Typography, Button, Space, Card, Avatar, Select } from 'antd'
import { PlayCircleOutlined, LeftOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { useApproverOnboardingContext } from '../context'

import IntlMessages from '../../../util/IntlMessages'

const { Title, Text } = Typography

interface ManualAddingStepProps {
  onBack?: () => void
}

export const ManualAddingStep: React.FC<ManualAddingStepProps> = ({ onBack }) => {
  const { nextStep, isLoading, currentStep } = useApproverOnboardingContext()
  const { formatMessage } = useIntl()

  return (
    <div className="approver-onboarding-step manual-adding-step">
      <div className="step-content">
        <div className="step-left-section">
          <div className="manual-content-container">
            {/* Back button */}
            {currentStep > 0 && <Button type="text" icon={<LeftOutlined />} onClick={onBack} className="step-back-button" />}

            <Text type="secondary" className="manual-step-indicator">
              2/3
            </Text>
            <Title level={2} className="manual-title">
              <IntlMessages id="approver.onboarding.manualAdd.title" />
            </Title>
            <Text className="manual-description">
              <IntlMessages id="approver.onboarding.manualAdd.description" />
            </Text>

            <Button type="primary" size="large" onClick={nextStep} loading={isLoading} className="manual-primary-button">
              <IntlMessages id="app.continue" />
            </Button>

            {/* Learn more video card */}
            <Card className="manual-video-card">
              <Space align="start" size={16}>
                <a
                  href="https://www.youtube.com/watch?v=96_HQExSugI"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Approving leave on Slack video"
                  className="manual-video-link"
                >
                  <img
                    src="https://img.youtube.com/vi/96_HQExSugI/hqdefault.jpg"
                    alt={formatMessage({ id: 'approver.onboarding.manualAdd.video.thumbnailAlt' })}
                    className="manual-video-thumbnail"
                  />
                  <PlayCircleOutlined className="manual-video-play-icon" />
                </a>
                <div>
                  <Text strong className="manual-video-title">
                    <IntlMessages id="approver.onboarding.manualAdd.video.title" />
                  </Text>
                  <Text type="secondary" className="manual-video-description">
                    <IntlMessages id="approver.onboarding.manualAdd.video.description" />
                  </Text>
                  <Button type="link" className="manual-video-learn-more" onClick={() => window.open('https://www.youtube.com/watch?v=96_HQExSugI', '_blank', 'noopener')}>
                    <IntlMessages id="approver.onboarding.manualAdd.video.learnMore" />
                  </Button>
                </div>
              </Space>
            </Card>
          </div>
        </div>

        <div className="step-right-section">
          <div className="request-illustration-container">
            {/* Mock form showing manual leave entry */}
            <Card className="manual-form-card">
              <Title level={4} className="manual-form-title">
                <IntlMessages id="approver.onboarding.manualAdd.form.selectUser" />
              </Title>

              <Select
                value="kaiya-septimus"
                className="manual-form-select"
                size="large"
                disabled
                options={[
                  {
                    value: 'kaiya-septimus',
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <img src="/approver-onboarding/user-large.svg" alt="Kaiya Septimus" style={{ width: 22, height: 22 }} />
                        <span>Kaiya Septimus</span>
                      </div>
                    ),
                  },
                ]}
              />

              <Title level={4} className="manual-form-hint-title">
                <IntlMessages id="approver.onboarding.manualAdd.form.dateAndTime" />
              </Title>

              <Card className="manual-form-date-card">
                <div className="manual-form-date-row">
                  <Text strong>
                    <IntlMessages id="approver.onboarding.manualAdd.form.dates" />
                  </Text>
                  <Text>January 4, 2025</Text>
                </div>
                <div className="manual-form-date-row-time">
                  <Text strong>
                    <IntlMessages id="app.from" />
                  </Text>
                  <Text>3 pm</Text>
                  <Text strong>
                    <IntlMessages id="app.to" />
                  </Text>
                  <Text>5 pm</Text>
                </div>
              </Card>

              <Title level={4} className="manual-form-hint-title">
                <IntlMessages id="approver.onboarding.manualAdd.form.reason" />
              </Title>

              <Card className="manual-form-reason-card">
                <Text>
                  <IntlMessages id="approver.onboarding.manualAdd.form.reasonPlaceholder" />
                </Text>
              </Card>

              <Title level={4} className="manual-form-hint-title">
                <IntlMessages id="approver.onboarding.manualAdd.form.hint" />
              </Title>

              <Card className="manual-form-result-card">
                <div className="manual-form-result-content">
                  <Avatar size={40} className="manual-form-result-avatar" src="/approver-onboarding/user-large.svg" />
                  <div>
                    <Text strong style={{ display: 'block' }}>
                      Kaiya Septimus
                    </Text>
                    <Text type="secondary">
                      <IntlMessages id="approver.onboarding.manualAdd.form.departmentPlaceholder" />
                    </Text>
                    <div className="manual-form-result-status">
                      <Text className="manual-form-result-status-text">
                        <IntlMessages id="approver.onboarding.manualAdd.form.statusApproved" />
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
